import React, { useCallback, useRef, useState } from 'react';
import { type ChangeEvent, type KeyboardEvent } from 'react';

import { type FindChangedEvent } from 'ag-grid-community';
import { type AgGridReact } from 'ag-grid-react';
import '../../../utils/mst.scss';

/** Defer Find API until after paint to avoid React update-depth issues with grouped rows (ag-grid-react GroupCellRenderer). */
function scheduleAgGridFind(run: () => void) {
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}

interface SearchBoxProps {
  gridRef: React.RefObject<AgGridReact>;
  findSearchValue?: string;
  onFindChanged?: (event: any) => void;
  setFindSearchValue?: (value: string) => void;
  activeMatchNum?: string;
  isVisible?: boolean;
  onToggle?: () => void;
  onShow?: () => void;
  onHide?: () => void;
}

function SearchBox({ 
  gridRef, 
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  setFindSearchValue: externalSetFindSearchValue,
  activeMatchNum: externalActiveMatchNum,
  isVisible = false,
  onToggle,
  onShow,
  onHide
}: SearchBoxProps) {
  const goToRef = useRef<HTMLInputElement>(null);
  const [internalFindSearchValue, setInternalFindSearchValue] = useState<string>();
  const [internalActiveMatchNum, setInternalActiveMatchNum] = useState<string>();

  // Use external values if provided, otherwise use internal state
  const findSearchValue = externalFindSearchValue ?? internalFindSearchValue;
  const activeMatchNum = externalActiveMatchNum ?? internalActiveMatchNum;

  const onFindChanged = useCallback((event: FindChangedEvent) => {
    const { activeMatch, totalMatches, findSearchValue: eventFindSearchValue } = event;
    const matchNum = eventFindSearchValue?.length
      ? `${activeMatch?.numOverall ?? 0}/${totalMatches}`
      : "";
    
    if (externalSetFindSearchValue && externalOnFindChanged) {
      // External control - let parent handle the state
      externalOnFindChanged(event);
    } else {
      // Internal control
      setInternalActiveMatchNum(matchNum);
    }
  }, [externalSetFindSearchValue, externalOnFindChanged]);

  const onInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (externalSetFindSearchValue) {
      externalSetFindSearchValue(value);
    } else {
      setInternalFindSearchValue(value);
    }
  }, [externalSetFindSearchValue]);

  const next = useCallback(() => {
    if (!gridRef.current?.api) return;
    scheduleAgGridFind(() => {
      gridRef.current?.api?.findNext();
    });
  }, [gridRef]);

  const previous = useCallback(() => {
    if (!gridRef.current?.api) return;
    scheduleAgGridFind(() => {
      gridRef.current?.api?.findPrevious();
    });
  }, [gridRef]);

  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const backwards = event.shiftKey;
      if (backwards) {
        previous();
      } else {
        next();
      }
    }
    
    // ESC to hide search and clear search value
    if (event.key === "Escape") {
      // Clear search value
      if (externalSetFindSearchValue) {
        externalSetFindSearchValue('');
      } else {
        setInternalFindSearchValue('');
      }
      // Clear AgGrid search
      if (gridRef.current?.api) {
        gridRef.current.api.setGridOption('findSearchValue', '');
      }
      onHide?.();
    }
  }, [next, previous, onHide, externalSetFindSearchValue, gridRef]);

  const goToFind = useCallback(() => {
    const num = Number(goToRef.current?.value);
    if (Number.isNaN(num) || num < 1 || !gridRef.current?.api) {
      return;
    }
    scheduleAgGridFind(() => {
      gridRef.current?.api?.findGoTo(num, true);
    });
  }, [gridRef]);

  const handleClose = useCallback(() => {
    // Clear search value
    if (externalSetFindSearchValue) {
      externalSetFindSearchValue('');
    } else {
      setInternalFindSearchValue('');
    }
    // Clear AgGrid search
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('findSearchValue', '');
    }
    onHide?.();
  }, [externalSetFindSearchValue, gridRef, onHide]);

  // Do not display if isVisible = false
  if (!isVisible) {
    return null;
  }

  return (
    <div className="search-box">
      <div className="search-controls">
        <span>検索:</span>
        <input 
          type="text" 
          onInput={onInput} 
          onKeyDown={onKeyDown}
          placeholder="検索する値を入力..."
          value={findSearchValue || ''}
        />
        <button type="button" onClick={previous}>
          前へ
        </button>
        <button type="button" onClick={next}>
          次へ
        </button>
        <span className="match-counter">{activeMatchNum}</span>
      </div>
      
      <div className="go-to-controls">
        <span>マッチへ移動:</span>
        <input 
          type="number" 
          ref={goToRef}
          placeholder="番号"
          />
        <button type="button" onClick={goToFind}>
          移動
        </button>
      </div>
        {onHide && (
          <button type="button" onClick={handleClose} className="close-button">
            閉じる
          </button>
        )}
    </div>
  );
}

export default SearchBox;
